import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import Lottie from 'react-lottie-player';
import { toast } from 'react-toastify';

import Icon from 'Components/Icon/Icon';
import Table from 'Components/Table/Table';
import { ReactComponent as LightImg } from '../../assets/MobileBuildsLight.svg';
import { ReactComponent as DarkImg } from '../../assets/MobileBuildsDark.svg';
import formatDate from 'Shared/util/formatDate';
import Button from 'Components/Button/Button';
import copyToClipboard from 'Shared/util/copyToClipboard';
import EmptyPage from 'Components/EmptyPage/EmptyPage';
import Tag from 'Components/Tag/Tag';
import animationData from 'Shared/spiner_blue.json';
import AddMobileBuildForm from '../AddMobileBuild/AddMobileBuild.js';
import Input from 'Components/Input/Input';
import RadioButtonGroup from 'Components/RadioButtonGroup/RadioButtonGroup';
import CustomRowComponent from 'Components/CustomRowComponent/CustomRowComponent';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { logger } from 'RootShared/utility/ErrorLogger';

const MOBILE_PAGE_SIZE = 3;
const DESKTOP_PAGE_SIZE = 6;

export const queryGetMobileBuilds = gql`
  query MobileBuildsQuery($offset: Int!, $limit: Int!, $clientContractId: Int!) @api(name: root) {
    builds(offset: $offset, limit: $limit, clientContractId: $clientContractId) {
      id
      system
      buildType
      shareLink
      description
      createdAt
      clientContract {
        id
        title
      }
    }
  }
`;

const transformMobileBuildsData = (builds) => {
  let ios = [];
  let android = [];
  builds.forEach((build) => {
    if (build.system === 'IOS') {
      ios.push({
        ...build,
        name: build.clientContract.title,
        status: build.buildType,
      });
    } else if (build.system === 'ANDROID') {
      android.push({
        ...build,
        name: build.clientContract.title,
        status: build.buildType,
      });
    }
  });

  return {
    ios: ios,
    android: android,
  };
};

const buildTypeFormat = (buildType) => {
  switch (buildType) {
    case 'DEV':
      return 'Develoment';
    case 'STAGING':
      return 'Staging';
    case 'TEST':
      return 'Test';
    case 'PROD':
      return 'Production';
    default:
      return '-';
  }
};

const buildsTypes = [
  { label: 'All', value: 'ALL' },
  { label: 'Staging', value: 'STAGING' },
  { label: 'Development', value: 'DEV' },
  { label: 'Test', value: 'TEST' },
  { label: 'Production', value: 'PROD' },
];

const TabMobileBuildsList = () => {
  const [activeSubproject, setActiveSubproject] = useState('ALL');
  const pageSize = window.innerWidth < 768 ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;
  const [isAddBuildModalOpen, setAddMobileBuildOpenStatus] = useState(false);
  const [filterDataStatus, setFilterDataStatus] = useState('New');
  const [sortBuilds, setSortBuilds] = useState(null);
  const { id } = useParams();
  const history = useHistory();

  const { data, loading, error } = useQuery(queryGetMobileBuilds, {
    variables: {
      offset: 0,
      limit: 40,
      clientContractId: +id,
    },
  });

  if (error) {
    logger.error(error);
    toast(error.message, {
      type: toast.TYPE.ERROR,
      autoClose: 3000,
      closeButton: true,
      hideProgressBar: false,
    });
  }

  const columns = useMemo(
    () => [
      {
        cellProps: {
          accessor: 'id',
          minWidth: 80,
        },
      },
      {
        cellProps: {
          accessor: 'name',
          minWidth: 173,
        },
      },
      {
        cellProps: {
          accessor: 'buildType',
          minWidth: 300,
        },
      },
      {
        title: '',
        cellProps: {
          Cell: (row) => {
            return (
              <div className="row__icons">
                <Tag className="icon-copy-tag" size="small" variant="gray" text="Copied" />
                <Icon
                  className="row-icon"
                  name="icon-copy"
                  onClick={(e) => {
                    copyToClipboard(`https://${window.location.host}/employee/builds/${row.original.shareLink}/`);
                    let emailTagClassList = e.target.offsetParent.getElementsByClassName('icon-copy-tag')[0].classList;
                    emailTagClassList.add('icon-copy-tag_active');
                    setTimeout(() => {
                      emailTagClassList.remove('icon-copy-tag_active');
                    }, 2000);
                    e.preventDefault();
                  }}
                />
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    window.location = 'tg://resolve';
                  }}
                  className="row-icon"
                >
                  <Icon name="icon-send" />
                </div>
                <div onClick={() => history.push(`/employee/builds/${row.original.shareLink}/`)} className="row-icon">
                  <Icon name="icon-right" dataTestId={'icon-right-mobile-builds'} />
                </div>
              </div>
            );
          },
          accessor: 'buidType',
        },
      },
    ],
    [history]
  );

  const handleOpenAddBuildModal = () => {
    setAddMobileBuildOpenStatus(!isAddBuildModalOpen);
  };

  useEffect(() => {
    let sortBuilds = [];
    if (data && data.builds) {
      if (filterDataStatus === 'Old') {
        sortBuilds = [...data.builds].sort((a, b) => {
          return new Date(a.createdAt) - new Date(b.createdAt);
        });
        setSortBuilds(sortBuilds);
      } else if (filterDataStatus === 'New') {
        sortBuilds = [...data.builds].sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setSortBuilds(sortBuilds);
      }
    }
  }, [filterDataStatus, data]);

  const modal = isAddBuildModalOpen ? (
    <AddMobileBuildForm
      projectName={document.querySelector('.tab-nav__item--active').textContent}
      data={data}
      handleOpenAddBuildModal={handleOpenAddBuildModal}
    />
  ) : null;

  const filterMobileBuilds = useCallback((data, attribute, value) => {
    return data.filter((element) => element[attribute] === value);
  }, []);
  const setBuildFilter = (type) => {
    setActiveSubproject(type.value);
  };

  const getTableComponent = useCallback(
    (data = []) => {
      if (data.length) {
        return (
          <Table
            {...{
              data: data.map((item) => ({
                ...item,
                id: {
                  title: item.id,
                },
                name: {
                  title: item.name,
                  subtitle: formatDate(item.createdAt),
                },
                buildType: {
                  title: buildTypeFormat(item.buildType),
                  subtitle: item.description,
                },
                date: {
                  title: formatDate(item.createdAt),
                },
                status: {
                  tagProps: {
                    text: item.status,
                    variant: item.status === 'Requested' ? 'yellow' : item.status === 'Rejected' ? 'red' : 'green',
                  },
                },
              })),
              columns,
              tableSettings: {
                headerHeight: 40,
                paginationHeight: 40,
                rowHeight: 88,
                tablePadding: 0,
                pageSize: pageSize,
                CustomRowComponent: (props) => (
                  <CustomRowComponent url={'/employee/builds/'} typeOfSearch={'publicHash'} {...props} />
                ),
              },
            }}
          />
        );
      }

      return (
        <Table
          sortable={false}
          {...{
            data: data.map((item) => ({
              ...item,
              name: {
                title: item.name,
                subtitle: formatDate(item.createdAt),
              },
              buildType: {
                title: buildTypeFormat(item.buildType),
                subtitle: item.version,
              },

              date: {
                title: formatDate(item.createdAt),
              },
              status: {
                tagProps: {
                  text: item.status,
                  variant: item.status === 'Requested' ? 'yellow' : item.status === 'Rejected' ? 'red' : 'green',
                },
              },
            })),
            columns,
            tableSettings: {
              headerHeight: 40,
              paginationHeight: 40,
              rowHeight: 88,
              tablePadding: 0,
              pageSize: pageSize,
            },
          }}
        />
      );
    },
    [columns, pageSize]
  );

  const filterNames = useMemo(() => {
    const names = [
      { label: 'New', active: false },
      { label: 'Old', active: false },
    ];
    return names.map((name) => {
      if (name.label === filterDataStatus) {
        return {
          ...name,
          active: true,
        };
      } else {
        return name;
      }
    });
  }, [filterDataStatus]);

  const handleFilterVisibility = () => {
    const navigation = document.querySelector('.mobile-builds-list__navigation');
    const list = document.querySelector('.mobile-builds__section');
    if (navigation.classList.contains('mobile-builds-list__navigation-none')) {
      list.classList.remove('mobile-builds__no-filter');
      navigation.classList.remove('mobile-builds-list__navigation-none');
    } else {
      list.classList.add('mobile-builds__no-filter');
      navigation.classList.add('mobile-builds-list__navigation-none');
    }
  };

  const getMobileBuildsTab = (mobileBuilds) => {
    return (
      <>
        {mobileBuilds.ios.length || mobileBuilds.android.length ? (
          <div data-testid="mobile-builds-list" className="container container_large mobile-builds">
            <Button className="navigation__button icon-filter" name="icon-filter" onClick={handleFilterVisibility} />
            <div className="mobile-builds-list__navigation">
              <Input variant="select" className="navigation__input" options={buildsTypes} callback={setBuildFilter} />
              <div className="navigation__filter-builds">
                <RadioButtonGroup names={filterNames} callback={setFilterDataStatus} />
              </div>
            </div>
            <Button
              dataTestId={'add-build'}
              icon="icon-plus"
              variant="primary"
              className="button__add-build"
              text="Add build"
              onClick={handleOpenAddBuildModal}
            />
            <div className="mobile-builds__section">
              <div className="header">
                <div className="header__section">
                  <Tag text="IOS" variant="purple" size="medium" />
                </div>
              </div>
              {getTableComponent(
                activeSubproject === 'ALL'
                  ? mobileBuilds.ios
                  : filterMobileBuilds(mobileBuilds.ios, 'status', activeSubproject)
              )}
            </div>
            <div className="mobile-builds__section">
              <div className="header">
                <div className="header__section">
                  <Tag text="Android" variant="green" size="medium" />
                </div>
              </div>
              {getTableComponent(
                activeSubproject === 'ALL'
                  ? mobileBuilds.android
                  : filterMobileBuilds(mobileBuilds.android, 'status', activeSubproject)
              )}
            </div>
          </div>
        ) : (
          <>
            <EmptyPage
              img={JSON.parse(localStorage.getItem('nightMode')) ? <DarkImg /> : <LightImg />}
              title={'No mobile builds yet'}
              text={"Don't worry, you can always add one!"}
            />
            <Button
              icon="icon-plus"
              variant="primary"
              className="button__add-first-build"
              text="Add build"
              onClick={handleOpenAddBuildModal}
            />
          </>
        )}
      </>
    );
  };

  return (
    <>
      {data?.builds && sortBuilds && getMobileBuildsTab(transformMobileBuildsData(sortBuilds))}
      {loading && (
        <Lottie
          data-testid="tab_expenses_history"
          className="lottie"
          loop={true}
          play={true}
          animationData={animationData}
          width={100}
          style={{ width: 100, height: 100, margin: '0px auto' }}
        />
      )}
      {modal}
    </>
  );
};

export default withErrorBoundary(TabMobileBuildsList);
